import { cn } from "@/lib/utils";

/**
 * The Downwind mark: a pattern circuit with the runway held inside it and the
 * downwind leg lit on the left edge.
 *
 * The geometry is copied verbatim from the brand kit's master
 * (`downwind-website/lib/mark.ts`, rendered to `brand/svg/mark.svg`) — same
 * 32×32 grid, same three paths, same stroke weights. Do not redraw it here; if
 * the mark ever changes it changes there first and this is re-copied, the same
 * way `shared/internal_contract.ts` mirrors aloftwx-api.
 *
 * What is deliberately NOT copied is the colour. The kit ships fixed hexes per
 * colourway (day `#c2185b`, night `#ff5fa2`) because its consumers are flat
 * images — an OG card cannot read a CSS variable. This console can, so the
 * circuit and runway take `currentColor` and the lit leg takes the console's
 * own `--marker`, which already resolves to `#ec2e8e` on paper and `#ff5cae` on
 * night stock. That keeps one magenta in the document instead of two that
 * almost match, and the mark inverts with the theme for free.
 *
 * Replaced `ArcusMark` (two isobars over a station circle) when the console
 * moved to console.downwind.studio — the console is no longer AloftWX-specific,
 * and a weather mark on it was a promise about scope that is no longer true.
 */
export function DownwindMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="butt"
      aria-hidden="true"
      focusable="false"
    >
      {/* The circuit — the thinnest line in the mark, so it reads as the path
          flown rather than as a container. */}
      <path d="M24 22V10Q24 5 19 5H13Q8 5 8 10V22Q8 27 13 27H19Q24 27 24 22Z" strokeWidth="1.4" />
      {/* The downwind leg, lit. The one place the marker appears in the rail. */}
      <path d="M8 10v12" strokeWidth="2.6" className="stroke-marker" />
      {/* The runway — the boldest thing in the mark. */}
      <path d="M16 11v10" strokeWidth="3.2" />
    </svg>
  );
}

/**
 * The mark and the name, set in the display face.
 *
 * `markOnly` drops the wordmark for the collapsed rail, where there is no room
 * for it. Nothing is lost to a screen reader: the only place the brand appears
 * is inside a link that carries its own aria-label, so the word is decoration.
 */
export function Brand({ className, markOnly = false }: { className?: string; markOnly?: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-2.5 text-ink", className)}>
      <DownwindMark className="size-5 shrink-0" />
      {!markOnly && (
        <span
          className="text-[1.05rem] font-bold"
          style={{
            fontFamily: "var(--font-display)",
            fontStretch: "105%",
            letterSpacing: "-0.025em",
          }}
        >
          Downwind
        </span>
      )}
    </span>
  );
}
